import { useState } from 'react';
import { Asset } from '@/api/generatedApi';
import { dateFormat } from '@/utils/format';

export const useFilterManagement = (allAssets: Asset[]) => {
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [temporaryFilters, setTemporaryFilters] = useState<Record<string, string[]>>({});
  const [filterAnchorElement, setFilterAnchorElement] = useState<HTMLElement>();
  const [activeFilterField, setActiveFilterField] = useState<string>();

  const filterPopoverOpen = Boolean(filterAnchorElement);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, fieldKey: string) => {
    setFilterAnchorElement(event.currentTarget);
    setActiveFilterField(fieldKey);
    setTemporaryFilters(previous => ({ ...previous, [fieldKey]: appliedFilters[fieldKey] || [] }));
  };

  const handleFilterClose = () => {
    setFilterAnchorElement(undefined);
    setActiveFilterField(undefined);
  };

  const handleFilterChange = (fieldKey: string, value: string, checked: boolean) => {
    setTemporaryFilters(previous => {
      const currentValues = previous[fieldKey] || [];
      return checked
        ? { ...previous, [fieldKey]: [...currentValues, value] }
        : { ...previous, [fieldKey]: currentValues.filter(v => v !== value) };
    });
  };

  const handleApplyFilters = () => {
    if (activeFilterField) {
      setAppliedFilters(previous => ({
        ...previous,
        [activeFilterField]: temporaryFilters[activeFilterField] || [],
      }));
    }
    handleFilterClose();
  };

  const handleClearFilter = () => {
    if (activeFilterField) {
      setTemporaryFilters(previous => ({ ...previous, [activeFilterField]: [] }));
      setAppliedFilters(previous => ({ ...previous, [activeFilterField]: [] }));
    }
    handleFilterClose();
  };

  const getFieldValues = (fieldKey: string) => {
    const values = new Set<string>();

    for (const asset of allAssets) {
      let value = '';
      switch (fieldKey) {
        case 'createdBy': {
          value = asset.creator?.email || '';
          break;
        }
        case 'folder': {
          const pathParts = asset.path?.split('/');
          value = pathParts && pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : 'none';
          break;
        }
        case 'created': {
          value = dateFormat(asset.createdAt);
          break;
        }
        default: {
          if (fieldKey.startsWith('metadata_')) {
            const metadataKey = fieldKey.replace('metadata_', '');
            value = String(asset.metadata?.[metadataKey] ?? '');
          }
          break;
        }
      }
      if (value) values.add(value);
    }

    return [...values].sort((a, b) => a.localeCompare(b));
  };

  return {
    appliedFilters,
    temporaryFilters,
    filterAnchorElement,
    activeFilterField,
    filterPopoverOpen,
    setTemporaryFilters,
    handleFilterClick,
    handleFilterClose,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilter,
    getFieldValues,
  };
};
